import {push} from 'react-router-redux'
import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import {isNil} from 'lodash'
import {iconNames} from 'components/icon'
import {urls} from 'helpers/url'
import {getFailureCode, getFailureMessage} from 'helpers/api'
import {loadInvoice} from 'actions/invoicing'
import types from '../actions/types'
import {snackbarMessage} from 'actions/snackbar'
import {invoicingContextInvoicesPivotTableKey} from 'reducers/invoicingContext/invoices'
import {invoicingContextPurchasingPivotTableKey} from 'reducers/invoicingContext/purchasing'
import {getSelectedInvoice} from 'selectors/invoices'
import {isPivotTableVisible} from 'selectors/pivotTable'
import {getSelectedPurchaseInvoice} from 'selectors/purchasing'
import {deSelectAllRows, requestData} from 'actions/pivotTable'
import {loadPurchaseInvoice} from 'actions/purchasing'
import {generalError} from "actions/index"
import {
  disableSage50Failure,
  disableSage50Success,
  enableSage50Failure,
  enableSage50Success,
  getCurrentAccountingProviderFailure,
  getCurrentAccountingProviderSuccess,
  getSage50ProviderFailure,
  getSage50ProviderSuccess,
  getTaxCodesFailure,
  getTaxCodesSuccess,
  hideInvoicePostReport,
  isLoading,
  postInvoiceAsyncSuccess,
  postPurchaseInvoiceAsyncSuccess,
  updateSageProviderFailure,
} from 'actions/accounting'

export default (api) => {

  function* postPurchaseInvoices({ids, updatedCompanies, reloadSelectedInvoice = false}) {
    yield call(postInvoicesAsync, {ids, updatedCompanies, reloadSelectedInvoice, isPurchase: true})
  }

  function* postInvoicesAsync({
                                ids,
                                updatedCompanies,
                                reloadSelectedInvoice = false,
                                isPurchase = false,
                              }) {
    try {
      yield put(isLoading(true))

      if (!isNil(updatedCompanies)) {
        const response = yield call(api.company.edit, {companies: updatedCompanies})
        if (!response.ok) {
          const message = getFailureMessage(response)
          const code = getFailureCode(response)

          yield put(hideInvoicePostReport())
          yield put(isLoading(false))
          yield put(snackbarMessage(
            'Problem updating companies. ' + code + ':' + message,
            'error',
            false))
        }
      }

      const apiCall = isPurchase
        ? api.accounting.purchaseInvoicePostAsync
        : api.accounting.invoicePostAsync

      const response = yield call(apiCall, {id: ids})

      yield api.handleResponse(
        response,
        function* onOk() {
          if (isPurchase) {
            yield put(postPurchaseInvoiceAsyncSuccess(ids))
            if (reloadSelectedInvoice) {
              const selectedPurchaseInvoice = yield select(getSelectedPurchaseInvoice)
              if (!isNil(selectedPurchaseInvoice)) yield put(loadPurchaseInvoice(selectedPurchaseInvoice.id))
            }
            const pivotTableIsVisible = yield select(state =>
              isPivotTableVisible(state, invoicingContextPurchasingPivotTableKey))
            if (pivotTableIsVisible) {
              yield put(requestData(invoicingContextPurchasingPivotTableKey))
              yield put(deSelectAllRows(invoicingContextPurchasingPivotTableKey))
            }
          } else {
            yield put(postInvoiceAsyncSuccess(ids))
            if (reloadSelectedInvoice) {
              const selectedInvoice = yield select(getSelectedInvoice)
              if (!isNil(selectedInvoice)) yield put(loadInvoice(selectedInvoice.id))
            }
            const pivotTableIsVisible = yield select(state =>
              isPivotTableVisible(state, invoicingContextInvoicesPivotTableKey))
            if (pivotTableIsVisible) {
              yield put(requestData(invoicingContextInvoicesPivotTableKey))
              yield put(deSelectAllRows(invoicingContextInvoicesPivotTableKey))
            }
          }
          const snackbarLabel = ids.length === 1
            ? 'Your invoice is being processed'
            : 'Your invoices are being processed'
          yield put(snackbarMessage(snackbarLabel, 'loading', true))
          yield put(isLoading(false))
        },
        function* onFail(key, message, code) {
          yield put(hideInvoicePostReport())
          yield put(isLoading(false))
          yield put(snackbarMessage(
            'Problem posting invoices. ' + code + ':' + message,
            'error',
            false))
        }
      )
    } catch (e) {
      yield put(snackbarMessage(
        'Could not connect to the accounting system. ' + e.toString(),
        'error',
        false))
      yield put(hideInvoicePostReport())
      yield put(isLoading(false))
    }
  }

  function* getTaxCodes() {
    const response = yield call(api.accounting.getTaxCodes)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(getTaxCodesSuccess(data.result))
      },
      function* onFail(key, message, code) {
        yield put(getTaxCodesFailure(message, code))
      }
    )

  }

  function* updateInvoicingContext() {
    const invoice = yield select(getSelectedInvoice)
    if (invoice) yield put(loadInvoice(invoice.id))
  }

  function* getCurrentAccountingProvider() {
    const response = yield call(api.accounting.getCurrentAccountingProvider)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(getCurrentAccountingProviderSuccess(data.result))
      },
      function* onFail(key, message, code) {
        yield put(getCurrentAccountingProviderFailure(message, code))
      }
    )
  }

  function* getSage50Provider() {
    const response = yield call(api.sage50.get)

    yield(api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(getSage50ProviderSuccess(data.result))
      },
      function* onFail(key, message, code) {
        yield put(getSage50ProviderFailure(message, code))
      }
    ))
  }

  function* updateSage50Provider({params}) {
    try {
      const response = yield call(api.sage50.update, params)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield call(getSage50Provider)
        },
        function* onFail(key, message, code) {
          yield put(updateSageProviderFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* enableSage50({params}) {
    const response = yield call(api.sage50.enableSage50, params)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(enableSage50Success(data.result))
        yield put(snackbarMessage('Successfully Connected to Sage 50', iconNames.tick))
        yield put(push(urls.myAccount.setupAccountingPackageUrl))
      },
      function* onFail(key, message, code) {
        yield put(enableSage50Failure(message, code))
        yield put(snackbarMessage('Failed to Connect to Sage 50', 'error', true))
      }
    )
  }

  function* disableSage50() {
    const response = yield call(api.sage50.disableSage50)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(disableSage50Success(data.result))
        yield put(snackbarMessage('Successfully Disconnected From Sage 50', iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(disableSage50Failure(message, code))
        yield put(snackbarMessage(
          'Problem Disconnecting from Sage 50. ' + code + ':' + message,
          'error',
          false))
      }
    )
  }

  function* updateSage50Data() {
    const response = yield call(api.sage50.updateData)
    yield api.handleResponse(
      response,
      function* onOk() {
        yield put(snackbarMessage(
          'Updating Sage 50 data, this may take a few minutes',
          iconNames.loading))
      },
      function* onFail(key, message, code) {
        yield put(snackbarMessage(
          'Problem updating Sage 50 data. ' + code + ':' + message,
          'error',
          false))
      }
    )
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.ACCOUNTING_INVOICE_POST_ASYNC, postInvoicesAsync),
      takeLatest(types.ACCOUNTING_PURCHASE_INVOICE_POST_ASYNC, postPurchaseInvoices),
      takeLatest(types.ACCOUNTING_TAX_CODE_READ_ATTEMPT, getTaxCodes),
      takeLatest(types.ACCOUNTING_INVOICE_HIDE_POST_REPORT, updateInvoicingContext),
      takeLatest(types.ACCOUNTING_CURRENT_PROVIDER_ATTEMPT, getCurrentAccountingProvider),
      takeLatest(types.ACCOUNTING_SAGE50_PROVIDER_READ_ATTEMPT, getSage50Provider),
      takeLatest(types.ACCOUNTING_SAGE50_PROVIDER_UPDATE_ATTEMPT, updateSage50Provider),
      takeLatest(types.ACCOUNTING_SAGE50_PROVIDER_ENABLE_ATTEMPT, enableSage50),
      takeLatest(types.ACCOUNTING_SAGE50_PROVIDER_DISABLE_ATTEMPT, disableSage50),
      takeLatest(types.ACCOUNTING_SAGE50_DATA_UPDATE_ATTEMPT, updateSage50Data),
    ])
  }

  function* watcher() {
    yield fork(myWatcher)
  }

  return {
    watcher,
  }
}
